import { createFileRoute, redirect } from "@tanstack/react-router";
import { resolveLegacyRedirect } from "@/lib/legacy-redirects";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { ProductCard, ProductCardSkeleton } from "@/components/storefront/ProductCard";
import { listProducts, listCategories, listOffers, listBestSellers, listProductsByIds, listHomeCategorySections } from "@/lib/products.functions";
import { Link } from "@tanstack/react-router";
import { buildCategoryIconMap, categorySlug } from "@/lib/category-icon";
import { CategoryIcon } from "@/components/storefront/CategoryIcon";
import { HomeCategorySection } from "@/components/storefront/HomeCategorySection";
import { ChristmasStrip } from "@/components/storefront/ChristmasStrip";
import { MrBoxBanner } from "@/components/storefront/MrBoxBanner";
import { MrPandaDeliveryBar } from "@/components/storefront/MrPandaFreeDelivery";
import { PandakiButton } from "@/components/storefront/PandakiButton";
import { useAuth } from "@/hooks/use-auth";
import { useWishlist } from "@/hooks/storefront/use-wishlist";
import { useRecentlyViewed } from "@/hooks/storefront/use-recently-viewed";
import { useMemo, useState, lazy, Suspense, useEffect, useRef, Fragment, type ReactNode } from "react";
import { useIsHydrated } from "@/hooks/use-hydrated";
import { useInViewport } from "@/hooks/use-in-viewport";
import { useIsMobile } from "@/hooks/use-mobile";
// section heading icons removed — headings now use only the animated emoji
import { sortHomeProducts } from "@/lib/product-sort";
import { mark, measureFromStart } from "@/lib/user-timing";
import heroMobileUrl from "@/assets/hero-mobile.webp?url";
import { useProductCountLabel } from "@/hooks/storefront/use-product-count";

// Below-the-fold widgets — split into their own chunks so their JS is not
// parsed/executed during the initial critical render (reduces main-thread JS).
const BrandShowcase = lazy(() =>
  import("@/components/storefront/BrandShowcase").then((m) => ({ default: m.BrandShowcase })),
);
const SocialToday = lazy(() =>
  import("@/components/storefront/SocialToday").then((m) => ({ default: m.SocialToday })),
);
// Ο hero slider είναι `hidden md:block`: σε κινητά δεν φαίνεται ποτέ, αλλά
// μέχρι τώρα γινόταν SSR + hydrate (μεγάλο component ~500 γραμμές) και χρέωνε
// άσκοπα το κύριο νήμα. Τώρα φορτώνεται lazy και ΜΟΝΟ σε desktop πλάτος.
const HomeHeroSlider = lazy(() =>
  import("@/components/storefront/HomeHeroSlider").then((m) => ({ default: m.HomeHeroSlider })),
);
import { DeferredMount } from "@/components/storefront/DeferredMount";

// Number of products requested per home row. The loading skeletons render the
// same count so the section height is identical before and after the data
// loads (keeps CLS ~0 now that rows are no longer awaited during SSR).
// Kept at 12 (= 2 full rows of 6 on desktop) so the home page ships fewer
// product-card DOM nodes on first load — large DOM size hurts style/layout
// recalculation cost. "Δες τα όλα" links lead to the full lists.
const HOME_ROW_LIMIT = 12;

const productsQO = queryOptions({
  queryKey: ["home", "products"],
  queryFn: () => listProducts({ data: { limit: HOME_ROW_LIMIT, sort: "new" } }),
  staleTime: 5 * 60_000,
});
const offersQO = queryOptions({
  queryKey: ["home", "offers"],
  queryFn: () => listOffers({ data: { limit: HOME_ROW_LIMIT } }),
  staleTime: 5 * 60_000,
});
const bestsellersQO = queryOptions({
  queryKey: ["home", "bestsellers"],
  queryFn: () => listBestSellers({ data: { limit: HOME_ROW_LIMIT } }),
  staleTime: 5 * 60_000,
});
const categoriesQO = queryOptions({
  queryKey: ["categories"],
  queryFn: () => listCategories(),
});

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://mrpanda.gr/" },
      { title: "MrPanda.gr — Eshop με χιλιάδες προϊόντα" },
      { name: "description", content: "Νέες αφίξεις, προσφορές και χιλιάδες προϊόντα στις καλύτερες τιμές. Ασφαλείς πληρωμές με Viva.com, Klarna, IRIS και τραπεζικό έμβασμα." },
      { property: "og:title", content: "MrPanda.gr — Eshop με χιλιάδες προϊόντα" },
      { property: "og:description", content: "Νέες αφίξεις, προσφορές και χιλιάδες προϊόντα στις καλύτερες τιμές. Ασφαλείς πληρωμές με Viva.com, Klarna, IRIS και τραπεζικό έμβασμα." },
    ],
    links: [
      { rel: "canonical", href: "https://mrpanda.gr/" },
      // LCP (mobile): η hero εικόνα ήταν ενσωματωμένη ως data URI, αλλά βρισκόταν
      // ~200KB μέσα στο SSR HTML (μετά το inline CSS), οπότε ο browser την
      // ανακάλυπτε πολύ αργά -> ~1.2s «καθυστέρηση απόδοσης». Ως κανονικό αρχείο
      // με preload υψηλής προτεραιότητας στην αρχή του head, κατεβαίνει
      // παράλληλα με το HTML/CSS και ζωγραφίζεται αμέσως μόλις φτάσει.
      { rel: "preload", as: "image", href: heroMobileUrl, fetchPriority: "high" },
    ],
  }),
  loader: async ({ context, location }) => {
    // Παλιά PrestaShop non-friendly URLs στο root (π.χ. `/?id_product=19025`,
    // `/?id_category=...`, `/?id_manufacturer=...`) χτυπούν αυτό το route αντί
    // για το catch-all `$.tsx`. Τα ανακατευθύνουμε με 301 στο νέο URL, ώστε να
    // μη φαίνεται 200 (homepage) στο Google για παλιά προϊόντα/κατηγορίες.
    const dest = resolveLegacyRedirect("/", location.searchStr ?? "");
    if (dest && dest !== "/") {
      throw redirect({ href: encodeURI(dest), statusCode: 301 });
    }
    // Kick off the categories fetch but DO NOT await it. Awaiting blocked the
    // whole SSR response (~4s) behind a slow categories query, which left the
    // user staring at a white screen (only the streamed shell + cookie banner
    // were visible) until the data resolved. Firing it off without awaiting
    // lets the page paint immediately; the chips fill in as the query resolves.
    void context.queryClient.prefetchQuery(categoriesQO);
    // IMPORTANT (LCP): do NOT await the product queries here. Awaiting them
    // blocked the whole route component — including the static hero <h1>, which
    // is the LCP element — behind ~4s of slow DB queries, producing a 4.3s
    // "element render delay" in PageSpeed. We only kick the queries off so the
    // client has a head start; the hero paints immediately in the streamed
    // shell. CLS stays low because each product row renders a skeleton grid
    // with the SAME item count as the final cards (see HOME_ROW_LIMIT), so the
    // section height does not change when the data swaps in.
    void context.queryClient.prefetchQuery(bestsellersQO);
    void context.queryClient.prefetchQuery(offersQO);
    void context.queryClient.prefetchQuery(productsQO);
  },
  component: HomePage,
});

function HomePage() {
  // Hydration-safe: ο loader κάνει prefetch ΧΩΡΙΣ await, οπότε ο server κάνει
  // render skeletons (τα queries είναι ακόμα pending). Τα δεδομένα όμως μπορεί
  // να προλάβουν να μπουν στο dehydrated cache, οπότε ο client θα έκανε hydrate
  // με τις πραγματικές κάρτες → hydration mismatch (React #418). Με το
  // `hydrated` flag, το ΠΡΩΤΟ client render δείχνει πάντα skeletons (ίδιο με τον
  // server) και τα δεδομένα μπαίνουν αμέσως μετά το mount (ίδιο ύψος → 0 CLS).
  const hydrated = useIsHydrated();
  const { data: prodRaw } = useQuery(productsQO);
  const { data: offersRaw } = useQuery(offersQO);
  const { data: bestRaw } = useQuery(bestsellersQO);
  const { data: catsRaw } = useQuery(categoriesQO);
  const prod = hydrated ? prodRaw : undefined;
  const offers = hydrated ? offersRaw : undefined;
  const best = hydrated ? bestRaw : undefined;
  const catsData = hydrated ? catsRaw : undefined;
  const cats = catsData ?? { categories: [], error: null };
  useMemo(() => {
    if (cats.categories.length > 0) buildCategoryIconMap(cats.categories);
  }, [cats.categories]);

  // User Timing: measure when the first product row's data becomes available
  // (a key "the store feels loaded" moment). Emitted once per mount.
  const productsTimed = useRef(false);
  useEffect(() => {
    if (productsTimed.current) return;
    if (best?.products?.length || offers?.products?.length || prod?.products?.length) {
      productsTimed.current = true;
      mark("home:products-loaded");
      // Duration from the browser's navigation start to first products painted.
      measureFromStart("home:time-to-products");
    }
  }, [best, offers, prod]);

  // User Timing: hydration ολοκληρωμένο (η σελίδα γίνεται διαδραστική).
  const hydrationTimed = useRef(false);
  useEffect(() => {
    if (hydrationTimed.current || !hydrated) return;
    hydrationTimed.current = true;
    mark("home:hydrated");
    measureFromStart("home:time-to-interactive");
  }, [hydrated]);

  // User Timing: πότε είναι έτοιμη η πλοήγηση κατηγοριών.
  const catsTimed = useRef(false);
  useEffect(() => {
    if (catsTimed.current) return;
    if (cats.categories.length > 0) {
      catsTimed.current = true;
      mark("home:categories-loaded");
      measureFromStart("home:time-to-categories");
    }
  }, [cats.categories.length]);

  const { user } = useAuth();
  const wishlist = useWishlist();
  const { ids: recentIds } = useRecentlyViewed();

  // Ταξινομημένες ενότητες κατηγοριών βάθους 2 (level_depth=3).
  const { data: sectionsRaw } = useQuery({
    queryKey: ["home", "category-sections"],
    queryFn: () => listHomeCategorySections(),
    staleTime: 10 * 60_000,
  });
  const sectionsData = hydrated ? sectionsRaw : undefined;

  // Οι ενότητες κατηγοριών είναι δεκάδες: αν μπουν όλες μαζί στο DOM, η αρχική
  // σελίδα φτάνει τα ~1.700 στοιχεία (ακριβοί υπολογισμοί στιλ/διάταξης).
  // Κρατάμε λίγες στο DOM και προσθέτουμε κι άλλες όσο ο χρήστης κατεβαίνει.
  const SECTIONS_STEP = 3;
  const [sectionLimit, setSectionLimit] = useState(SECTIONS_STEP);
  const moreRef = useRef<HTMLDivElement | null>(null);
  const allSections = sectionsData?.sections ?? [];
  useEffect(() => {
    const el = moreRef.current;
    if (!el || sectionLimit >= allSections.length) return;
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSectionLimit((n) => n + SECTIONS_STEP);
        }
      },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [sectionLimit, allSections.length]);

  // Χάρτης απογόνων ανά κατηγορία (για να φέρνουμε προϊόντα όλου του υποδέντρου).
  const descendantsByPsId = useMemo(() => {
    const childrenByPsId = new Map<number, number[]>();
    for (const c of cats.categories) {
      if (c.parent_ps_id == null) continue;
      const arr = childrenByPsId.get(c.parent_ps_id) ?? [];
      arr.push(c.ps_id);
      childrenByPsId.set(c.parent_ps_id, arr);
    }
    const collect = (root: number): number[] => {
      const out = [root];
      const stack = [root];
      while (stack.length) {
        const cur = stack.pop()!;
        for (const ch of childrenByPsId.get(cur) ?? []) {
          out.push(ch);
          stack.push(ch);
        }
      }
      return out;
    };
    const map = new Map<number, number[]>();
    for (const c of cats.categories) map.set(c.ps_id, collect(c.ps_id));
    return map;
  }, [cats.categories]);

  const productCountLabel = useProductCountLabel();
  return (
    <StorefrontLayout>
      <div className="home-playful">
      {/* Mobile-only hero heading. The desktop slider below is `hidden md:block`,
          so on mobile (what Lighthouse measures) there was NO server-rendered
          above-the-fold content — the client-only cookie banner became the LCP
          element with a huge "render delay". This static block is part of the
          initial SSR HTML, so it paints at TTFB and becomes the LCP element with
          ~0ms render delay. */}
      <section className="relative z-10 bg-gradient-to-br from-accent to-background px-4 py-6 md:hidden">
        <h1 className="text-2xl font-extrabold leading-tight text-foreground">
          Υπάρχουν διαθέσιμα <span className="text-primary">{productCountLabel} προϊόντα</span>!
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Σίγουρα θα βρεις αυτό που ψάχνεις στην καλύτερη τιμή.
        </p>
        {/* Eager, server-rendered hero image — this is the mobile LCP element.
            Because it is in the initial SSR HTML and preloaded with high
            priority (see head().links), it paints as soon as it downloads, so
            the LCP time is spent loading the resource instead of waiting on JS
            hydration. Explicit width/height keep CLS at ~0. */}
        <img
          src={heroMobileUrl}
          alt="Ο MrPanda τρέχει με τσάντες αγορών και δώρα"
          width={480}
          height={320}
          loading="eager"
          fetchPriority="high"
          decoding="sync"
          className="mt-4 aspect-[3/2] w-full rounded-2xl object-cover"
        />
      </section>

      <DesktopHero offers={offers} best={best} />

      <MrPandaDeliveryBar />

      <ChristmasStrip index={0} />

      <div className="home-bands">
      {user && (
        <PersonalRow
          title="Είδες πρόσφατα"
          emoji="🕒"
          ids={recentIds}
        />
      )}

      {user && (
        <PersonalRow
          title="Αγαπημένα προϊόντα"
          emoji="❤️"
          ids={wishlist.items.map((i) => i.productId)}
        />
      )}

      <ProductRow
        title="Κορυφαία σε Πωλήσεις"
        emoji="🔥"
        to="/best-sellers"
        data={best}
        priorityCount={2}
        right={<PandakiButton />}
      />

      <ProductRow
        title="Προσφορές"
        emoji="🏷️"
        to="/offers"
        data={offers}
        defer
      />

      <ProductRow
        title="Νέες αφίξεις"
        emoji="✨"
        to="/new-products"
        data={prod}
        defer
      />

      <MrBoxBanner />

      {catsData === undefined ? (
        <section className="mx-auto max-w-none px-4 py-8">
          <h2 className="mb-4 text-lg font-bold">Δημοφιλείς κατηγορίες</h2>
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="h-10 w-28 animate-pulse rounded-full bg-muted" />
            ))}
          </div>
        </section>
      ) : cats.categories.length > 0 ? (
        <section className="mx-auto max-w-none px-4 py-8">
          <h2 className="mb-4 text-lg font-bold">Δημοφιλείς κατηγορίες</h2>
          <div className="flex flex-wrap gap-2">
            {cats.categories.slice(0, 8).map((c) => (
              <CategoryChip key={c.id} psId={c.ps_id} name={c.name} />
            ))}
          </div>
        </section>
      ) : null}

      {sectionsData === undefined ? (
        <section className="mx-auto max-w-none px-4 py-8">
          <div className="mb-4 h-7 w-48 animate-pulse rounded bg-muted" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        </section>
      ) : (
        allSections.slice(0, sectionLimit).map((s, i) => (
          <Fragment key={s.ps_id}>
            {i > 0 && i % 3 === 0 && <ChristmasStrip className="py-2" />}
            <HomeCategorySection
              psId={s.ps_id}
              name={s.name}
              descendantIds={descendantsByPsId.get(s.ps_id) ?? [s.ps_id]}
              limit={12}
            />
          </Fragment>
        ))
      )}

      {sectionsData && sectionLimit < allSections.length && (
        <div ref={moreRef} className="h-24" aria-hidden />
      )}

      <LazyInView minHeight={220}>
        <Suspense fallback={<div className="min-h-[220px]" aria-hidden />}>
          <BrandShowcase />
        </Suspense>
      </LazyInView>

      <DeferredMount>
        <Suspense fallback={null}>
          <SocialToday />
        </Suspense>
      </DeferredMount>
      </div>
      </div>
    </StorefrontLayout>
  );
}

/** Αποδίδει τα παιδιά μόνο όταν πλησιάσουν στο viewport (λιγότερο JS στο load). */
function LazyInView({ children, minHeight }: { children: React.ReactNode; minHeight: number }) {
  const { ref, visible } = useInViewport<HTMLDivElement>();
  return (
    <div ref={ref} style={{ minHeight }}>
      {visible ? children : null}
    </div>
  );
}

function PersonalRow({
  title,
  emoji,
  ids,
}: {
  title: string;
  emoji: string;
  ids: string[];
}) {
  const limited = useMemo(() => ids.slice(0, 6), [ids]);
  const { data } = useQuery({
    queryKey: ["home", "byids", title, limited.join(",")],
    queryFn: () => listProductsByIds({ data: { ids: limited } }),
    enabled: limited.length > 0,
    staleTime: 60_000,
  });
  const products = data?.products ?? [];
  if (limited.length === 0) return null;
  if (data && products.length === 0) return null;
  return (
    <section className="mx-auto max-w-none px-4 py-8">
      <div className="mb-4 flex items-end justify-between">
        <h2 className="flex items-center gap-2 text-lg font-bold">
          <span className="animate-bob" aria-hidden>{emoji}</span>
          {title}
        </h2>
      </div>
      {products.length === 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {limited.map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {products.map((p) => <ProductCard key={p.id} product={p as never} />)}
        </div>
      )}
    </section>
  );
}

type HomeProductData = { products: unknown[]; error: string | null } | undefined;

/**
 * Αποδίδει σταδιακά (ανά frame) τα στοιχεία μιας λίστας, ώστε το React να μην
 * κάνει ένα τεράστιο commit — σπάει τις «μεγάλες εργασίες» του κύριου νήματος.
 */
function useProgressiveCount(total: number, step = 3) {
  const [n, setN] = useState(() => Math.min(total, step));
  useEffect(() => {
    if (n >= total) return;
    const raf = requestAnimationFrame(() => setN(Math.min(total, n + step)));
    return () => cancelAnimationFrame(raf);
  }, [n, total, step]);
  return Math.min(n, total);
}

/**
 * Αποδίδει τον hero slider μόνο σε desktop πλάτος και μόνο μετά το hydration.
 * Στα κινητά (όπου ο slider είναι έτσι κι αλλιώς κρυφός) δεν κατεβαίνει καθόλου
 * το chunk του — λιγότερο parse/compile/execute JS στο κύριο νήμα.
 */
function DesktopHero({ offers, best }: { offers: HomeProductData; best: HomeProductData }) {
  const isMobile = useIsMobile();
  const hydrated = useIsHydrated();
  if (!hydrated || isMobile) {
    return <div className="hidden md:block md:h-[260px]" aria-hidden />;
  }
  return (
    <Suspense fallback={<div className="hidden md:block md:h-[260px]" aria-hidden />}>
      <HomeHeroSlider offers={offers as never} best={best as never} />
    </Suspense>
  );
}

function ProductRow({
  title,
  emoji,
  to,
  data,
  priorityCount = 0,
  defer = false,
  right,
}: {
  title: string;
  emoji: string;
  to: "/best-sellers" | "/offers" | "/new-products";
  data: HomeProductData;
  /** Number of leading cards to render with eager/high-priority images (LCP). */
  priorityCount?: number;
  /** When true, the card grid mounts only once the row scrolls near the
   *  viewport, keeping its React render/execution work out of the initial load. */
  defer?: boolean;
  /** Προσάρμομα στο δεξί άκρο της κεφαλίδας της σειράς (π.χ. κουμπί Pandaki). */
  right?: ReactNode;
}) {
  const products = data?.products ?? [];
  const { ref, visible } = useInViewport<HTMLElement>();
  // Στα κινητά αποδίδουμε τις μισές κάρτες: λιγότερα components σημαίνει
  // λιγότερο hydration/JS execution στο main thread.
  // Λιγότερες κάρτες ανά σειρά => σημαντικά μικρότερο DOM (style/layout cost)
  // τόσο σε κινητά όσο και σε desktop. Το "Δες τα όλα" οδηγεί στην πλήρη λίστα.
  const cardLimit = 6;
  // Until the row is near the viewport, keep a skeleton grid of the SAME item
  // count/height (zero CLS) instead of hydrating the real cards.
  const showCards = !defer || visible;
  const visibleProducts = useMemo(
    () => sortHomeProducts(products as never).slice(0, cardLimit),
    [products, cardLimit],
  );
  // Σταδιακή απόδοση καρτών ανά frame: αποφεύγει ένα μεγάλο task hydration.
  const shown = useProgressiveCount(showCards ? visibleProducts.length : 0, 3);
  return (
    <section ref={ref} className="mx-auto max-w-none px-4 py-8">
      <div className="mb-4 flex items-end justify-between">
        <h2 className="flex items-center gap-2 text-lg font-bold">
          <span className="animate-bob" aria-hidden>{emoji}</span>
          {title}
        </h2>
        <div className="flex items-center gap-3">
          {right}
          <Link to={to} aria-label={`Δες τα όλα — ${title}`} className="text-sm text-link hover:underline">Δες τα όλα →</Link>
        </div>
      </div>
      {!data || !showCards ? (
        // Ελαφρύ placeholder αντί για 12 skeleton κάρτες: ίδιο ύψος (μηδενικό
        // CLS) αλλά ελάχιστα στοιχεία DOM και σχεδόν καθόλου κόστος hydration.
        <div
          aria-hidden
          className="min-h-[560px] rounded-xl bg-muted/30 sm:min-h-[380px] lg:min-h-[300px]"
        />
      ) : products.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-muted-foreground">
          {data.error ? `Σφάλμα: ${data.error}` : "Δεν βρέθηκαν προϊόντα."}
        </div>
      ) : (
        <div className="grid min-h-[560px] grid-cols-2 gap-3 sm:min-h-[380px] sm:grid-cols-3 lg:min-h-[300px] lg:grid-cols-6">
          {visibleProducts.slice(0, shown).map((p, i) => (
            <ProductCard
              key={(p as { id: string }).id}
              product={p as never}
              priority={i < priorityCount}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CategoryChip({ psId, name }: { psId: number; name: string }) {
  return (
    <Link
      to="/c/$slug"
      params={{ slug: categorySlug(psId, name) }}
      className="mp-bouncy inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm hover:border-primary hover:text-primary"
    >
      <CategoryIcon psId={psId} className="h-5 w-5" />
      {name}
    </Link>
  );
}
